(() => {
  'use strict';
  if (window.__odHomeInitialized) return;
  window.__odHomeInitialized = true;
  const root = document.documentElement;
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  const scheme = matchMedia('(prefers-color-scheme: dark)');
  let transitionTimer;
  let explicitChoice = false;
  try { explicitChoice = ['light', 'dark'].includes(localStorage.getItem('od-appearance')); } catch (_) { /* Storage is optional. */ }
  const motionAllowed = () => !reduced.matches && root.dataset.odMotion !== 'false';
  function synchronizeButtons() {
    const dark = root.dataset.odTheme === 'dark';
    document.querySelectorAll('.od-theme-toggle').forEach(button => {
      button.setAttribute('aria-pressed', String(dark));
      button.setAttribute('aria-label', dark ? 'Cambiar a tema claro' : 'Cambiar a tema oscuro');
    });
  }
  function setTheme(theme, animate = false) {
    if (!['light', 'dark'].includes(theme)) return;
    clearTimeout(transitionTimer);
    root.classList.toggle('od-theme-changing', animate && motionAllowed());
    root.dataset.odTheme = theme;
    synchronizeButtons();
    transitionTimer = setTimeout(() => root.classList.remove('od-theme-changing'), 240);
  }
  document.addEventListener('click', event => {
    if (!event.target.closest('.od-theme-toggle')) return;
    const next = root.dataset.odTheme === 'dark' ? 'light' : 'dark';
    explicitChoice = true;
    try { localStorage.setItem('od-appearance', next); } catch (_) { /* Keep the in-session selection. */ }
    setTheme(next, true);
  });
  window.addEventListener('storage', event => {
    if (event.key !== 'od-appearance') return;
    explicitChoice = ['light', 'dark'].includes(event.newValue);
    const fallback = root.dataset.odDefault === 'system' ? (scheme.matches ? 'dark' : 'light') : root.dataset.odDefault;
    setTheme(explicitChoice ? event.newValue : fallback);
  });
  scheme.addEventListener('change', () => {
    if (!explicitChoice && root.dataset.odDefault === 'system') setTheme(scheme.matches ? 'dark' : 'light');
  });
  let observer;
  const seen = new WeakSet();
  function prepareReveals(scope = document) {
    if (!motionAllowed() || !('IntersectionObserver' in window)) return;
    observer ||= new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (!entry.isIntersecting) return;
        observer.unobserve(entry.target);
        if (!motionAllowed() || seen.has(entry.target)) return;
        seen.add(entry.target);
        entry.target.animate([{ opacity: .65, transform: 'translateY(14px)' }, { opacity: 1, transform: 'translateY(0)' }], { duration: 440, easing: 'cubic-bezier(.2,.65,.25,1)' });
      });
    }, { threshold: .08 });
    scope.querySelectorAll('.od-reveal').forEach(element => { if (!seen.has(element)) observer.observe(element); });
  }
  reduced.addEventListener('change', () => {
    if (reduced.matches) {
      root.classList.remove('od-theme-changing');
      document.getAnimations().forEach(animation => { if (animation.effect?.target?.classList?.contains('od-reveal')) { try { animation.finish(); } catch (_) { animation.cancel(); } } });
    } else prepareReveals();
  });
  document.addEventListener('shopify:section:load', event => { synchronizeButtons(); prepareReveals(event.target); });
  document.addEventListener('shopify:section:unload', event => event.target.querySelectorAll('.od-reveal').forEach(element => observer?.unobserve(element)));
  class ODHeader extends HTMLElement {
    connectedCallback() {
      this.clearDesktopMenuTimers();
      this.controller?.abort();
      this.headerObserver?.disconnect();
      this.overlayObserver?.disconnect();
      cancelAnimationFrame(this.headerFrame);
      this.headerFrame = 0;
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.headerGroup = this.closest('#header-group') || this;
      this.headerSlot = this.closest('.od-header-slot');
      this.editorSectionSelected = false;
      this.editorBlockSelected = false;
      this.geometryDirty = false;
      this.resetDirection = false;
      this.dataset.pinned = 'false';
      this.dataset.hidden = 'false';
      this.resetScrollBaseline();
      this.measureHeader();
      this.updateScrollState(true);
      const measure = () => this.scheduleHeaderUpdate(true, true);
      if ('ResizeObserver' in window) {
        this.headerObserver = new ResizeObserver(measure);
        this.headerObserver.observe(this);
        if (this.headerGroup !== this) this.headerObserver.observe(this.headerGroup);
      }
      window.addEventListener('resize', measure, { signal, passive: true });
      window.addEventListener('scroll', () => this.scheduleHeaderUpdate(), { signal, passive: true });
      window.addEventListener('pageshow', () => this.scheduleHeaderUpdate(true, true), { signal });
      document.fonts?.addEventListener('loadingdone', measure, { signal });
      this.addEventListener('focusin', () => this.revealHeader(), { signal });
      this.addEventListener('focusout', () => this.scheduleHeaderUpdate(false, true), { signal });
      const dialogChanged = event => {
        if (event.target.matches?.('dialog') || this.contains(event.target)) this.scheduleHeaderUpdate(false, true);
      };
      document.addEventListener('toggle', dialogChanged, { signal, capture: true });
      document.addEventListener('close', dialogChanged, { signal, capture: true });
      // The attribute observer also covers native dialog implementations without toggle events.
      if ('MutationObserver' in window) {
        this.overlayObserver = new MutationObserver(records => {
          if (records.some(({ target, attributeName }) => attributeName === 'open'
            ? target.matches?.('dialog') || this.contains(target)
            : target === this || target === this.headerSlot)) {
            this.scheduleHeaderUpdate(true, true);
          }
        });
        this.overlayObserver.observe(document.documentElement, {
          subtree: true, attributes: true,
          attributeFilter: ['open', 'data-scroll-mode', 'data-scroll-threshold', 'data-scroll-tolerance'],
        });
      }
      const ownsEditorEvent = event => event.target === this || this.contains(event.target) || event.target.contains?.(this);
      document.addEventListener('shopify:section:select', event => {
        this.editorSectionSelected = Boolean(ownsEditorEvent(event));
        if (this.editorSectionSelected) this.revealHeader();
        this.scheduleHeaderUpdate(false, true);
      }, { signal });
      document.addEventListener('shopify:section:deselect', event => {
        if (ownsEditorEvent(event)) { this.editorSectionSelected = false; this.scheduleHeaderUpdate(false, true); }
      }, { signal });
      document.addEventListener('shopify:block:select', event => {
        this.editorBlockSelected = Boolean(ownsEditorEvent(event));
        if (this.editorBlockSelected) this.revealHeader();
        this.scheduleHeaderUpdate(false, true);
      }, { signal });
      document.addEventListener('shopify:block:deselect', event => {
        if (ownsEditorEvent(event)) { this.editorBlockSelected = false; this.scheduleHeaderUpdate(false, true); }
      }, { signal });
      document.addEventListener('shopify:section:load', event => {
        if (ownsEditorEvent(event)) this.scheduleHeaderUpdate(true, true);
      }, { signal });
      this.dialog = this.querySelector('.od-mobile-menu');
      this.opener = this.querySelector('.od-menu-open');
      const close = () => this.dialog?.close();
      this.addEventListener('click', event => {
        if (event.target.closest('.od-menu-open')) {
          if (!this.dialog?.open) {
            this.dialog.showModal();
            this.opener.setAttribute('aria-expanded', 'true');
            this.savedOverflow = document.body.style.overflow;
            document.body.style.overflow = 'hidden';
          }
        } else if (event.target.closest('.od-menu-close')) close();
        else if (event.target.closest('.od-search-trigger')) {
          const search = document.getElementById('search-modal');
          if (typeof search?.showDialog === 'function') { event.preventDefault(); search.showDialog(); }
        } else if (event.target.closest('.od-mobile-menu a')) close();
      }, { signal });
      this.dialog?.addEventListener('click', event => {
        if (event.target !== this.dialog) return;
        const rect = this.dialog.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) close();
      }, { signal });
      this.dialog?.addEventListener('close', () => this.restoreMenu(), { signal });
      this.addEventListener('keydown', event => {
        if (event.key !== 'Escape' || this.dialog?.open) return;
        const detail = event.target.closest('details[open]');
        if (detail) { detail.open = false; detail.querySelector('summary')?.focus(); }
      }, { signal });
      this.desktop = matchMedia('(min-width: 1024px)');
      this.setupDesktopMenus(signal);
      this.desktop.addEventListener('change', () => {
        if (this.desktop.matches) close();
        else this.closeDesktopMenus();
      }, { signal });
    }
    setupDesktopMenus(signal) {
      this.desktopNav = this.querySelector('.od-desktop-nav');
      this.desktopMenuStates = new Map();
      this.menuInput = 'keyboard';
      if (!this.desktopNav) return;
      const detailFor = target => {
        const detail = target.closest?.('details');
        return detail && this.desktopNav.contains(detail) ? detail : null;
      };
      const keyboardFocusInside = detail => detail.contains(document.activeElement)
        && this.desktopMenuState(detail).input !== 'pointer';
      // Tab can enter from outside the navigation. Track it before the resulting focusin.
      document.addEventListener('keydown', event => {
        this.menuInput = 'keyboard';
        const detail = detailFor(event.target);
        if (detail) {
          this.desktopMenuState(detail).input = 'keyboard';
          this.cancelDesktopMenuClose(detail);
        }
      }, { signal, capture: true });
      this.desktopNav.addEventListener('pointerdown', event => {
        if (!this.desktop.matches) return;
        this.menuInput = 'pointer';
        const detail = detailFor(event.target);
        if (detail) {
          this.desktopMenuState(detail).input = 'pointer';
          this.cancelDesktopMenuClose(detail);
        }
      }, { signal });
      this.desktopNav.addEventListener('click', event => {
        if (!this.desktop.matches) return;
        if (event.target.closest('a[href]')) { this.closeDesktopMenus(); return; }
        const summary = event.target.closest('summary');
        const detail = summary && detailFor(summary);
        if (!detail) return;
        // A keyboard/assistive click has no pointerdown and a zero click count.
        this.desktopMenuState(detail).input = event.detail === 0 ? 'keyboard' : this.menuInput;
        this.cancelDesktopMenuClose(detail);
        if (!detail.open) this.closeDesktopMenus(detail);
      }, { signal });
      this.desktopNav.addEventListener('toggle', event => {
        if (!this.desktop.matches || !event.target.matches?.('details')) return;
        if (event.target.open) this.closeDesktopMenus(event.target);
        else this.cancelDesktopMenuClose(event.target);
      }, { signal, capture: true });
      const hoverPointer = event => ['mouse', 'pen'].includes(event.pointerType) && event.buttons === 0;
      this.desktopNav.addEventListener('pointerenter', event => {
        if (hoverPointer(event) && event.target.matches?.('details')) this.cancelDesktopMenuClose(event.target);
      }, { signal, capture: true });
      this.desktopNav.addEventListener('pointerleave', event => {
        const detail = event.target;
        if (!this.desktop.matches || !hoverPointer(event) || !detail.matches?.('details') || !detail.open) return;
        this.cancelDesktopMenuClose(detail);
        if (keyboardFocusInside(detail)) return;
        const state = this.desktopMenuState(detail);
        state.timer = setTimeout(() => {
          state.timer = null;
          if (signal.aborted || !this.isConnected || !detail.isConnected || !this.desktop.matches || keyboardFocusInside(detail)) return;
          this.closeDesktopMenu(detail);
        }, 200);
      }, { signal, capture: true });
      this.desktopNav.addEventListener('focusin', event => {
        const detail = detailFor(event.target);
        if (!detail) return;
        this.desktopMenuState(detail).input = this.menuInput;
        this.cancelDesktopMenuClose(detail);
      }, { signal });
      this.desktopNav.addEventListener('focusout', event => {
        const detail = detailFor(event.target);
        if (!detail || detail.contains(event.relatedTarget)) return;
        queueMicrotask(() => {
          if (signal.aborted || !this.isConnected || !this.desktop.matches) return;
          if (!detail.contains(document.activeElement)) this.closeDesktopMenu(detail);
        });
      }, { signal });
      document.addEventListener('click', event => {
        if (!this.desktopNav.contains(event.target)) this.closeDesktopMenus();
      }, { signal });
      if (!this.desktop.matches) this.closeDesktopMenus();
    }
    desktopMenuState(detail) {
      let state = this.desktopMenuStates.get(detail);
      if (!state) {
        state = { timer: null, input: 'keyboard' };
        this.desktopMenuStates.set(detail, state);
      }
      return state;
    }
    cancelDesktopMenuClose(detail) {
      const state = this.desktopMenuStates?.get(detail);
      if (state?.timer) clearTimeout(state.timer);
      if (state) state.timer = null;
    }
    closeDesktopMenu(detail) {
      this.cancelDesktopMenuClose(detail);
      detail.open = false;
    }
    closeDesktopMenus(except) {
      this.desktopNav?.querySelectorAll('details').forEach(detail => {
        if (detail !== except) this.closeDesktopMenu(detail);
      });
    }
    clearDesktopMenuTimers() {
      this.desktopMenuStates?.forEach(state => { if (state.timer) clearTimeout(state.timer); state.timer = null; });
      this.desktopMenuStates?.clear();
    }
    scrollPosition() {
      const maximum = Math.max(0, Math.max(root.scrollHeight, document.body.scrollHeight) - window.innerHeight);
      // Safari rubber-band scrolling must not reverse the intended reveal direction.
      return Math.min(maximum, Math.max(0, window.scrollY || 0));
    }
    resetScrollBaseline(y = this.scrollPosition()) {
      this.previousScrollY = y;
      this.scrollDirection = 0;
      this.scrollTravel = 0;
    }
    measureHeader() {
      if (!this.isConnected) return;
      const pinned = this.dataset.pinned === 'true';
      // Read the natural box and margins, never the translated visual position.
      // The slot keeps the same flow space when the fixed state is restored in this task.
      if (pinned) this.dataset.pinned = 'false';
      const style = getComputedStyle(this);
      const marginTop = parseFloat(style.marginTop) || 0;
      const margin = marginTop + (parseFloat(style.marginBottom) || 0);
      const height = Math.max(0, this.offsetHeight + margin);
      const slotHeight = `${height}px`;
      if (this.headerSlot && this.headerSlot.style.getPropertyValue('--od-header-slot-height') !== slotHeight) this.headerSlot.style.setProperty('--od-header-slot-height', slotHeight);
      // Document coordinates need the raw scroll offset, including Safari overscroll.
      this.naturalHeaderTop = this.headerSlot
        ? this.headerSlot.getBoundingClientRect().top + (window.scrollY || 0) + marginTop : 0;
      const configuredTop = parseFloat(style.getPropertyValue('--od-sticky-top'));
      this.stickyTop = Number.isFinite(configuredTop) ? configuredTop : 12;
      const panel = this.querySelector('.od-header__panel');
      const groupHeight = this.headerGroup.offsetHeight;
      if (this.headerSlot) {
        // Resolve CSS max(env(safe-area-inset-top), setting) at the current viewport size.
        this.dataset.pinned = 'true';
        const resolvedTop = parseFloat(getComputedStyle(this).top);
        if (Number.isFinite(resolvedTop)) this.stickyTop = resolvedTop;
        this.dataset.pinned = String(pinned);
      }
      this.pinBoundary = Math.max(0, this.naturalHeaderTop - this.stickyTop);
      const panelHeight = `${panel?.offsetHeight || 0}px`;
      const measuredGroupHeight = `${groupHeight}px`;
      if (document.body.style.getPropertyValue('--header-height') !== panelHeight) document.body.style.setProperty('--header-height', panelHeight);
      if (document.body.style.getPropertyValue('--header-group-height') !== measuredGroupHeight) document.body.style.setProperty('--header-group-height', measuredGroupHeight);
      if (document.body.style.getPropertyValue('--transparent-header-offset-boolean') !== '0') document.body.style.setProperty('--transparent-header-offset-boolean', '0');
    }
    scheduleHeaderUpdate(geometry = false, reset = false) {
      if (!this.isConnected) return;
      this.geometryDirty ||= geometry;
      this.resetDirection ||= reset;
      if (this.headerFrame) return;
      this.headerFrame = requestAnimationFrame(() => {
        this.headerFrame = 0;
        if (!this.isConnected) return;
        const shouldReset = this.resetDirection;
        this.resetDirection = false;
        if (this.geometryDirty) { this.geometryDirty = false; this.measureHeader(); }
        this.updateScrollState(shouldReset);
      });
    }
    setScrollState(name, value) {
      if (this.dataset[name] !== value) this.dataset[name] = value;
    }
    revealHeader(y = this.scrollPosition()) {
      this.setScrollState('hidden', 'false');
      this.resetScrollBaseline(y);
    }
    updateScrollState(reset = false) {
      if (!this.isConnected) return;
      const mode = this.headerSlot ? (this.dataset.scrollMode || this.headerSlot.dataset.scrollMode || 'static') : 'static';
      const readSetting = (name, fallback) => {
        const value = Number.parseFloat(this.dataset[name] ?? this.headerSlot?.dataset[name]);
        return Number.isFinite(value) ? Math.max(0, value) : fallback;
      };
      const threshold = readSetting('scrollThreshold', 120);
      const tolerance = Math.max(1, readSetting('scrollTolerance', 8));
      const y = this.scrollPosition();
      const delta = y - this.previousScrollY;
      this.previousScrollY = y;
      if (mode === 'static' || !['sticky', 'reveal'].includes(mode) || y <= this.pinBoundary) {
        this.setScrollState('pinned', 'false');
        this.setScrollState('hidden', 'false');
        this.resetScrollBaseline(y);
        return;
      }
      this.setScrollState('pinned', 'true');
      const locked = this.contains(document.activeElement) || this.querySelector('details[open]')
        || document.querySelector('dialog[open]') || this.editorSectionSelected || this.editorBlockSelected;
      // The setting controls when hiding may begin; fixation starts at the natural sticky edge.
      if (reset || mode === 'sticky' || locked || y <= threshold) { this.revealHeader(y); return; }
      if (!delta) return;
      const direction = Math.sign(delta);
      this.scrollTravel = direction === this.scrollDirection ? this.scrollTravel + Math.abs(delta) : Math.abs(delta);
      this.scrollDirection = direction;
      if (this.scrollTravel >= tolerance) {
        this.setScrollState('hidden', String(direction > 0));
        this.scrollTravel = 0;
      }
    }
    restoreMenu() {
      if (this.savedOverflow !== undefined) { document.body.style.overflow = this.savedOverflow; this.savedOverflow = undefined; }
      this.opener?.setAttribute('aria-expanded', 'false');
      if (this.isConnected && this.opener?.getClientRects().length) this.opener.focus();
    }
    disconnectedCallback() {
      this.clearDesktopMenuTimers();
      this.headerObserver?.disconnect();
      this.overlayObserver?.disconnect();
      cancelAnimationFrame(this.headerFrame);
      this.headerFrame = 0;
      this.controller?.abort();
      this.dialog?.close();
      this.restoreMenu();
    }
  }
  if (!customElements.get('od-header')) customElements.define('od-header', ODHeader);
  synchronizeButtons();
  prepareReveals();
})();
